import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// About hero — faithful rewrite of legacy molecules/AboutNewHero (Strapi
// sections.about-new-hero). A light gradient band with a bottom-right rounded
// corner: big heading on the left, image overhanging the bottom-right on
// desktop (clipped by the band's overflow), stacked on mobile.
export const aboutHeroSchema = z.object({
  heading: z.string().min(1),
  imageUrl: z.string().optional(),
});

type AboutHeroProps = z.infer<typeof aboutHeroSchema>;

const Band = styled.section`
  position: relative;
  overflow: hidden;
  border-radius: 0 0 80px 0;
  background: linear-gradient(93deg, #e8efff 0%, #fff 100%);
  padding: 0 2.4rem 4.8rem;
`;

const Inner = styled.div`
  position: relative;
  max-width: 112rem;
  margin: 0 auto;
  padding-top: 10rem;

  @media (min-width: 62em) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-bottom: 4rem;
  }
`;

const Heading = styled.h1`
  margin: 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 3.4rem;
  letter-spacing: -3px;

  @media (min-width: 48em) {
    font-size: 7.2rem;
  }
`;

const HeroImage = styled.img`
  display: block;
  width: 100%;
  max-width: 100%;
  margin-top: 2.4rem;
  margin-bottom: -4.8rem;
  object-fit: contain;

  @media (min-width: 62em) {
    position: absolute;
    right: 0;
    bottom: -8.5rem;
    width: 72rem;
    max-width: 72rem;
    max-height: 50rem;
    height: 40rem;
    margin: 0;
  }
`;

function AboutHero({ heading, imageUrl }: AboutHeroProps) {
  return (
    <Band>
      <Inner>
        <div>
          <Heading>{heading}</Heading>
        </div>
        {imageUrl ? <HeroImage src={imageUrl} alt="" /> : null}
      </Inner>
    </Band>
  );
}

export const aboutHeroBlock: BlockDefinition<AboutHeroProps> = {
  type: 'aboutHero',
  schema: aboutHeroSchema,
  Renderer: AboutHero,
};
